import { api, setTokens, clearTokens, type TokenPair } from './client';

export type BackendRole = 'FARMER' | 'HTX_STAFF' | 'CO' | 'DEPT_HEAD' | 'DIRECTOR' | 'ACCOUNTANT' | 'OPS' | 'ADMIN';

export interface NotificationPrefs {
  loanUpdates?: boolean;
  debtReminders?: boolean;
  harvestPrice?: boolean;
}

export interface MeResponse {
  id: string;
  phone: string;
  fullName: string;
  role: BackendRole;
  htxId?: string;
  status: 'ACTIVE' | 'SUSPENDED';
  mfaEnabled: boolean;
  /** Whether a Zalo identity is attached. The id itself is never sent. */
  zaloLinked?: boolean;
  farmerId?: string;
  avatarUrl?: string;
  language?: 'vi' | 'en';
  fontSize?: 0 | 1 | 2;
  notificationPrefs?: NotificationPrefs;
}

export interface UpdateMePayload {
  fullName?: string;
  avatarUrl?: string;
  language?: 'vi' | 'en';
  fontSize?: 0 | 1 | 2;
  notificationPrefs?: NotificationPrefs;
}

export interface UpdateMeResponse {
  id: string;
  fullName: string;
  avatarUrl?: string;
  language?: 'vi' | 'en';
  fontSize?: 0 | 1 | 2;
  notificationPrefs?: NotificationPrefs;
}

export async function login(phone: string, password: string, mfaCode?: string): Promise<TokenPair> {
  const tokens = await api.post<TokenPair>('/auth/login', { phone, password, mfaCode });
  setTokens(tokens);
  return tokens;
}

export async function registerFarmer(data: { phone: string; password: string; fullName: string; htxId?: string }): Promise<{ id: string; phone: string; role: BackendRole }> {
  return api.post('/auth/register', data);
}

/** Self-service HTX registration — creates the Htx and its first HTX_STAFF
 * account together, atomically, immediately active (same trust model as
 * Farmer self-registration above). */
export async function registerHtx(data: {
  name: string;
  code: string;
  address?: string;
  phone: string;
  password: string;
  fullName: string;
}): Promise<{ id: string; phone: string; role: BackendRole; htxId: string }> {
  return api.post('/auth/register-htx', data);
}

// ── Zalo Login (đăng nhập / đăng ký bằng Zalo)
//
// The Zalo access token never reaches this app: we ask the backend for an
// authorize URL, Zalo redirects back to /dang-nhap?code=…&state=…, and the
// backend redeems that pair. The `zaloId` likewise only ever travels inside the
// server-signed `signupToken` — nothing here can claim to be a given Zalo user.

export interface ZaloAuthUrl {
  url: string;
}

export type ZaloCallbackResult =
  | ({ status: 'authenticated' } & TokenPair)
  | { status: 'registration_required'; signupToken: string; suggestedName?: string; pictureUrl?: string }
  | { status: 'linked' };

export interface ZaloRegisterPayload {
  signupToken: string;
  role: 'FARMER' | 'HTX';
  phone: string;
  fullName: string;
  /** Set only when `phone` already has an account: its password, which is what
   * lets the server attach this Zalo identity to it and log the caller in
   * instead of creating anything. */
  password?: string;
  /** Alongside `password`, for accounts that have MFA switched on. */
  mfaCode?: string;
  htxName?: string;
  htxCode?: string;
  htxAddress?: string;
}

/** `code` on the 409 that means: this phone already has an account, ask for its
 * password. Matched on instead of the message text, which is prose. */
export const ZALO_PHONE_TAKEN = 'PHONE_ALREADY_REGISTERED';

export async function zaloAuthUrl(): Promise<ZaloAuthUrl> {
  return api.get<ZaloAuthUrl>('/auth/zalo/url');
}

/** Stores the token pair itself when the callback logs the user straight in. */
export async function zaloCallback(code: string, state: string): Promise<ZaloCallbackResult> {
  const result = await api.post<ZaloCallbackResult>('/auth/zalo/callback', { code, state });
  if (result.status === 'authenticated') setTokens(result);
  return result;
}

/** Authenticated: begins attaching Zalo to the signed-in account. */
export async function zaloLinkUrl(): Promise<ZaloAuthUrl> {
  return api.get<ZaloAuthUrl>('/auth/zalo/link-url');
}

export async function zaloUnlink(): Promise<void> {
  await api.delete<void>('/auth/zalo/link');
}

export async function zaloRegister(payload: ZaloRegisterPayload): Promise<TokenPair> {
  const tokens = await api.post<TokenPair>('/auth/zalo/register', payload);
  setTokens(tokens);
  return tokens;
}

export async function fetchMe(): Promise<MeResponse> {
  return api.get<MeResponse>('/auth/me');
}

export async function updateMe(payload: UpdateMePayload): Promise<UpdateMeResponse> {
  return api.patch<UpdateMeResponse>('/auth/me', payload);
}

/** Rotates the session: the server revokes the caller's other sessions and issues a
 * fresh token pair, so we store it immediately — otherwise the current session's
 * next request would 401 against its now-revoked access token. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<TokenPair> {
  const tokens = await api.post<TokenPair>('/auth/change-password', { currentPassword, newPassword });
  setTokens(tokens);
  return tokens;
}

export async function logout(refreshToken: string): Promise<void> {
  try {
    await api.post('/auth/logout', { refreshToken });
  } finally {
    clearTokens();
  }
}
